/**
 * A game-agnostic description of where a round's problem pool comes from,
 * plus a pure interpreter (`buildProblemSet`) that resolves it into the
 * `topEQs`/`bottomAnswers` pools `nextProblem` populates the grid from.
 *
 * The `table` kind is a fixed, already-resolved dataset (used by VIFAddSub
 * and VIFMulDiv). The `generate` kind reject-samples problems from a pure
 * `GenerateRule` (used by VIFRulesAddSub's flag 5/11/12 procedural rounds).
 * Other kinds (e.g. carry-forward chains, draw-based pools) are added by
 * later steps as the games that need them are migrated.
 */

/** The round's equation/answer pools, as consumed by `BoardGameEngine.nextProblem`. */
export interface ProblemSet {
  topEQs: string[];
  bottomAnswers: string[];
}

/** A concrete fixed dataset (already resolved from any roll/rotation). */
export interface TableRef {
  topEQs: readonly string[];
  bottomAnswers: readonly string[];
}

/** Matches the shape of `Chance.randomInt(min, max)` (inclusive bounds). */
export type Rng = (min: number, max: number) => number;

/** One candidate operand pair plus its computed answer. */
export interface GenerateCandidate {
  // Operand 1
  v1: number;
  // Operand 2
  v2: number;
  // Answer (v1 + v2 or v1 - v2)
  a: number;
}

/**
 * A pure, named, unit-testable reject-sampling rule: draws a candidate,
 * decides whether to reject and re-draw, and renders an accepted candidate
 * into an equation/answer pair.
 */
export interface GenerateRule {
  readonly id: string;
  /** Draws one candidate; called once up front and again per rejection. */
  sample(this: void, rng: Rng): GenerateCandidate;
  /** True => reject and re-sample. */
  rejects(c: GenerateCandidate): boolean;
  render(c: GenerateCandidate): { topEQ: string; bottomAnswer: string };
}

export type ProblemSource =
  | { kind: 'table'; ref: TableRef }
  | { kind: 'generate'; rule: GenerateRule; count: number };
// (carryForward | draw kinds are added by later steps.)

/**
 * Generates `count` problems by reject-sampling `rule`: for each problem,
 * draws one candidate via `rule.sample`, then redraws while
 * `rule.rejects(candidate)` holds, then renders the accepted candidate.
 * Pure given `rng`.
 */
export function generateProblems(
  rule: GenerateRule,
  count: number,
  rng: Rng,
): ProblemSet {
  const topEQs: string[] = [];
  const bottomAnswers: string[] = [];
  for (let i = 0; i < count; i++) {
    let candidate = rule.sample(rng);
    while (rule.rejects(candidate)) {
      candidate = rule.sample(rng);
    }
    const rendered = rule.render(candidate);
    topEQs.push(rendered.topEQ);
    bottomAnswers.push(rendered.bottomAnswer);
  }
  return { topEQs, bottomAnswers };
}

/**
 * Pure interpreter: turns a ProblemSource into the round's pools.
 *
 * `rng` is required for the `generate` kind (throws if omitted) and unused
 * for `table`.
 */
export function buildProblemSet(source: ProblemSource, rng?: Rng): ProblemSet {
  switch (source.kind) {
    case 'table':
      return {
        topEQs: [...source.ref.topEQs],
        bottomAnswers: [...source.ref.bottomAnswers],
      };
    case 'generate':
      if (!rng) {
        throw new Error(
          `buildProblemSet: a 'generate' source (rule "${source.rule.id}") requires an rng.`,
        );
      }
      return generateProblems(source.rule, source.count, rng);
  }
}

/** Draws `v1, v2` independently from `rng(1, 99)` and sets `a = v1 + v2`. */
function sampleAddPair(rng: Rng): GenerateCandidate {
  const v1 = rng(1, 99);
  const v2 = rng(1, 99);
  return { v1, v2, a: v1 + v2 };
}

function renderAdd(c: GenerateCandidate) {
  return { topEQ: `${c.v1} + ${c.v2}`, bottomAnswer: String(c.a) };
}

function renderSub(c: GenerateCandidate) {
  return { topEQ: `${c.v1} - ${c.v2}`, bottomAnswer: String(c.a) };
}

function additionRequiresCarrying(c: GenerateCandidate) {
  return (c.v1 % 10) + (c.v2 % 10) > 9;
}

function additionRequiresNoCarrying(c: GenerateCandidate) {
  return (c.v1 % 10) + (c.v2 % 10) < 11;
}

function bothSingleDigit(c: GenerateCandidate) {
  return c.v1 < 10 && c.v2 < 10;
}

/**
 * VIFRulesAddSub gameFlag 5, round 1: two-digit sums under 100, no carry
 * (ones digits don't sum past 9).
 */
export const flag5Round1: GenerateRule = {
  id: 'flag5Round1',
  sample: sampleAddPair,
  rejects: (c) => c.a > 99 || additionRequiresCarrying(c) || bothSingleDigit(c),
  render: renderAdd,
};

/**
 * VIFRulesAddSub gameFlag 5, round 2: sums that cross 100, no carry in the
 * ones column.
 */
export const flag5Round2: GenerateRule = {
  id: 'flag5Round2',
  sample: sampleAddPair,
  rejects: (c) =>
    c.a < 100 || additionRequiresCarrying(c) || bothSingleDigit(c),
  render: renderAdd,
};

/**
 * VIFRulesAddSub gameFlag 5, round 3: two-digit sums under 100, requires a
 * carry (ones digits sum to 11+).
 */
export const flag5Round3: GenerateRule = {
  id: 'flag5Round3',
  sample: sampleAddPair,
  rejects: (c) =>
    c.a > 99 || additionRequiresNoCarrying(c) || bothSingleDigit(c),
  render: renderAdd,
};

/**
 * VIFRulesAddSub gameFlag 5, round 4: sums of at least 120, requires a carry.
 */
export const flag5Round4: GenerateRule = {
  id: 'flag5Round4',
  sample: sampleAddPair,
  rejects: (c) =>
    c.a < 120 || additionRequiresNoCarrying(c) || bothSingleDigit(c),
  render: renderAdd,
};

/**
 * VIFRulesAddSub gameFlag 11: subtraction with no borrow (v1's ones digit > v2's ones digit).
 * Re-sampling redraws both v1 and v2; v2's upper bound depends on the freshly drawn v1.
 */
export const flag11: GenerateRule = {
  id: 'flag11',
  sample: (rng) => {
    const v1 = rng(21, 99);
    const v2 = rng(10, v1 - 11);
    return { v1, v2, a: v1 - v2 };
  },
  rejects: (c) => c.v1 % 10 <= c.v2 % 10,
  render: renderSub,
};

/**
 * VIFRulesAddSub gameFlag 12, rounds 1-2: subtraction requiring a borrow (v1's ones digit < v2's ones digit).
 */
export const flag12Strict: GenerateRule = {
  id: 'flag12Strict',
  sample: (rng) => {
    const v1 = rng(12, 99);
    const v2 = rng(10, v1 - 1);
    return { v1, v2, a: v1 - v2 };
  },
  rejects: (c) => c.v1 % 10 >= c.v2 % 10,
  render: renderSub,
};

/**
 * VIFRulesAddSub gameFlag 12, rounds 3-4: same sampling as `flag12Strict`
 * but the borrow/no-borrow condition is mixed, so the original only ever
 * accepts the first draw (no reject loop runs).
 */
export const flag12Mixed: GenerateRule = {
  id: 'flag12Mixed',
  sample: flag12Strict.sample,
  rejects: () => false,
  render: renderSub,
};

export const RULES_ADD_SUB_GENERATORS = {
  flag5Round1,
  flag5Round2,
  flag5Round3,
  flag5Round4,
  flag11,
  flag12Strict,
  flag12Mixed,
};

/**
 * Resolves VIFAddSub's layer-2..4 pattern-group letter (A/B/C) from the
 * current layer and the round's single `randomPatterns` roll (0-5),
 * encoding the legacy truth table from `VIFAddSub.populateProblem` as a
 * pure lookup so it can be unit-tested directly.
 */
export function resolveAddSubPatternGroup(
  layer: number,
  roll: number,
): 'A' | 'B' | 'C' {
  if (
    (layer == 2 && (roll == 0 || roll == 1)) ||
    (layer == 3 && (roll == 2 || roll == 4)) ||
    (layer == 4 && (roll == 3 || roll == 5))
  ) {
    return 'A';
  }
  if (
    (layer == 2 && (roll == 2 || roll == 3)) ||
    (layer == 3 && (roll == 0 || roll == 5)) ||
    (layer == 4 && (roll == 1 || roll == 4))
  ) {
    return 'B';
  }
  return 'C';
}

/**
 * The A/B/C pattern table `resolveAddSubPatternGroup` selects for a layer and
 * roll, looked up on any tree that carries all three.
 */
export function addSubPatternTable(
  tree: Record<'patternGroupA' | 'patternGroupB' | 'patternGroupC', TableRef>,
  layer: number,
  roll: number,
): TableRef {
  return tree[`patternGroup${resolveAddSubPatternGroup(layer, roll)}`];
}
